import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import ErrorDetailsAccordion from "../components/ui/ErrorDetailsAccordion";
import { useBatchProcesses } from "../hooks/useBatchProcesses";
import { getBatchProcessLogs, getStorageInfoForBatch, getBatchProcessLive } from "../api/adminApi";
import {
  STATUS_META, STATUS_FILTER_OPTIONS,
  StatCard, StatusBadge, PipelineSteps, FileTypeBadge,
  TrashIcon, RestoreIcon, CheckIcon, XIcon,
  getDatabaseName, getFileType, getCreatedDate, getRegisteringUser, getStatus, timeAgo, getArchiveFileName,
} from "./batchProcessHelpers";
import { Hero, SegControl } from "../components/adminSurface/AdminSurface";
import { formatPath, joinPath } from "../utils/pathFormat";
import { LayersArt } from "../components/adminSurface/heroArt";

// ─── Chevron icon ──────────────────────────────────────────────────────────────

function ChevronIcon({ expanded }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 0.18s", transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Expanded detail panel ─────────────────────────────────────────────────────

const LOG_LEVEL_STYLE = {
  Error:   { color: "#b91c1c", bg: "#fee2e2", border: "#fecaca" },
  Warning: { color: "#92400e", bg: "#fef3c7", border: "#fde68a" },
  Info:    { color: "#1e40af", bg: "#eff6ff", border: "#bfdbfe" },
};

// ── Shared helpers ─────────────────────────────────────────────────────────────
const fmtNum = n => n == null ? null : Number(n).toLocaleString();
const fmtBytes = n => {
  if (n == null || n === 0) return null;
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576)    return `${(n / 1048576).toFixed(2)} MB`;
  if (n >= 1024)       return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
};
const fmtDuration = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms <= 0) return null;
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
};
const fmtTs = iso => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }) : null;

// ── SVG icons ──────────────────────────────────────────────────────────────────
const IconFolder = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
const IconFile   = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>;
const IconZap    = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const IconClock  = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;

// ── Storage info panel — flat card design ──────────────────────────────────────
export function StorageInfoPanel({ storageInfos, error, unlinkedWorkerRows = 0, expectsStorage = false }) {
  const { t } = useTranslation();

  // A failed load and an empty result used to render identically — as nothing at all —
  // which made a missing archive card impossible to diagnose from the screen. Say which
  // one happened, and when nothing is linked to a batch that should have archived
  // something, name the reason the server found.
  if (error) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
        {t("batches.storage.loadFailed", "Could not load archive details: {{error}}", { error })}
      </div>
    );
  }

  if (!storageInfos || storageInfos.length === 0) {
    if (!expectsStorage || unlinkedWorkerRows <= 0) return null;
    const count = unlinkedWorkerRows > 50 ? "50+" : String(unlinkedWorkerRows);
    return (
      <div style={{ marginTop: 8, borderRadius: 10, background: "#fffbeb", border: "1px solid #fcd34d", padding: "10px 14px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 3 }}>
          {t("batches.storage.noneLinked", "No archive record is linked to this batch.")}
        </div>
        <div style={{ fontSize: 11.5, color: "#78350f", lineHeight: 1.5 }}>
          {t("batches.storage.unlinkedHint",
             "{{count}} archive record(s) on this document base carry no batch link. That is written only by an ingestion worker older than this build — update the worker on the ingest server. Batches already archived by the old worker keep no link.",
             { count })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {storageInfos.map((s, i) => {
        const archivePath = (s.storageFolder || s.fileName)
          ? joinPath(s.storageFolder, s.fileName)
          : null;
        const ratioNum = s.compressRatio != null
          ? Number(s.compressRatio)
          : (s.totalSize && s.compressedSize) ? (s.compressedSize / s.totalSize * 100) : null;
        // Clamp to 0: when the archive is not smaller than the input (e.g. an already
        // incompressible source) the "saved" figure would go negative — show 0 instead.
        const saved = s.totalSize != null && s.compressedSize != null
          ? Math.max(0, s.totalSize - s.compressedSize) : null;
        const compPct = s.totalSize && s.compressedSize
          ? Math.min(100, (s.compressedSize / s.totalSize) * 100) : null;
        const duration = fmtDuration(s.startTime, s.finishTime);

        const stats = [
          { label: "Records",    value: fmtNum(s.docCount),         color: "#2563eb" },
          { label: "Pages",      value: fmtNum(s.pageCount),        color: "#7c3aed" },
          { label: "Original",   value: fmtBytes(s.totalSize),      color: "#0369a1" },
          { label: "Compressed", value: fmtBytes(s.compressedSize), color: "#0891b2" },
          s.resourceSize > 0
            ? { label: "Resources",  value: fmtBytes(s.resourceSize),  color: "#b45309" }
            : null,
          ratioNum != null
            ? { label: "Ratio",      value: `${ratioNum.toFixed(1)}%`, color: ratioNum < 60 ? "#059669" : "#64748b" }
            : null,
        ].filter(Boolean);

        return (
          <div key={s.id ?? i} style={{ marginBottom: 8, borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", overflow: "hidden" }}>

            {/* ── Paths ──────────────────────────────────────────────── */}
            {archivePath && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f1f5f9" }}>
                <span style={{ color: "#3b82f6", marginTop: 2, flexShrink: 0 }}><IconFolder /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 2 }}>Archive</div>
                  <div style={{ fontSize: 11.5, fontFamily: "monospace", color: "#1e293b", wordBreak: "break-all", lineHeight: 1.4 }}>{archivePath}</div>
                </div>
              </div>
            )}
            {s.uploadFile && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f1f5f9" }}>
                <span style={{ color: "#8b5cf6", marginTop: 2, flexShrink: 0 }}><IconFile /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 2 }}>Source</div>
                  <div style={{ fontSize: 11.5, fontFamily: "monospace", color: "#1e293b", wordBreak: "break-all", lineHeight: 1.4 }}>{s.uploadFile}</div>
                </div>
              </div>
            )}

            {/* ── Stats row ──────────────────────────────────────────── */}
            {stats.length > 0 && (
              <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9" }}>
                {stats.map((st, idx) => (
                  <div key={st.label} style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", padding: "10px 6px", textAlign: "center",
                    borderRight: idx < stats.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: st.color, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 4 }}>{st.value}</div>
                    <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8" }}>{st.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Compression bar ────────────────────────────────────── */}
            {compPct != null && (
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, color: "#64748b" }}>
                    <span style={{ fontWeight: 700 }}>{fmtBytes(s.compressedSize)}</span>
                    <span style={{ color: "#94a3b8" }}> compressed from </span>
                    <span style={{ fontWeight: 700 }}>{fmtBytes(s.totalSize)}</span>
                  </span>
                  {saved != null && (
                    <span style={{ fontSize: 10, fontWeight: 700,
                      color: saved > 0 ? "#059669" : "#64748b",
                      background: saved > 0 ? "#dcfce7" : "#f1f5f9",
                      borderRadius: 99, padding: "1px 8px" }}>
                      {saved > 0 ? `↓ ${fmtBytes(saved)} saved` : "0 B saved"}
                    </span>
                  )}
                </div>
                <div style={{ height: 6, borderRadius: 99, background: "#e2e8f0", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${compPct}%`, background: "linear-gradient(90deg,#38bdf8,#818cf8)", borderRadius: 99, transition: "width 0.5s ease" }} />
                </div>
              </div>
            )}

            {/* ── Timing row ─────────────────────────────────────────── */}
            {(s.startTime || s.finishTime) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px" }}>
                <span style={{ color: "#94a3b8", flexShrink: 0 }}><IconClock /></span>
                <span style={{ fontSize: 10, color: "#64748b" }}>
                  {fmtTs(s.startTime) ?? "—"}
                </span>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                {duration && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: "#6366f1", background: "#ede9fe", borderRadius: 99, padding: "2px 8px", flexShrink: 0 }}>
                    <IconZap />{duration}
                  </span>
                )}
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                <span style={{ fontSize: 10, color: "#64748b" }}>
                  {fmtTs(s.finishTime) ?? "—"}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BatchDetailPanel({ batch, colSpan, token, t }) {
  const batchId      = batch.id ?? batch.Id;
  const archivePath  = formatPath(batch.pathToArchiveFile ?? batch.PathToArchiveFile);
  // Which machine staged that file. In a multi-node deployment this is the answer to
  // "why did THIS worker not pick it up" — the path only exists on the staging host.
  const ownerHost    = batch.ownerHostName ?? batch.OwnerHostName ?? null;
  const indexPath    = formatPath(batch.pathToIndexFile   ?? batch.PathToIndexFile);
  const errorMsg     = batch.errorMessage           ?? batch.ErrorMessage;
  const startedAt    = batch.processingStartedAt    ?? batch.ProcessingStartedAt;
  const completedAt  = batch.processingCompletedAt  ?? batch.ProcessingCompletedAt;
  const propStatus   = getStatus(batch);

  // ── Live progress state ──────────────────────────────────────────────
  // While a batch is InProgress we poll its live fields every 5s so the panel
  // shows "Step: X · 143 docs · last activity 4s ago" without waiting for the
  // 30s page-level refresh. Initialised from the parent-supplied batch object;
  // updated in place when polling sees a newer LastActivityAt.
  const [liveStep,    setLiveStep]    = useState(batch.currentStep        ?? batch.CurrentStep        ?? null);
  const [liveDocs,    setLiveDocs]    = useState(batch.docsProcessedSoFar ?? batch.DocsProcessedSoFar ?? 0);
  const [liveTs,      setLiveTs]      = useState(batch.lastActivityAt     ?? batch.LastActivityAt     ?? null);
  const [liveStatus,  setLiveStatus]  = useState(propStatus);
  const status   = liveStatus || propStatus;
  const isFailed = status === "Failed";

  const [logs, setLogs]                 = useState(null);
  const [logsErr, setLogsErr]           = useState(null);
  const [expanded, setExpanded]         = useState({});
  const [storageInfos, setStorageInfos] = useState(null);
  const [storageErr, setStorageErr]     = useState(null);
  const [unlinkedRows, setUnlinkedRows] = useState(0);

  // Tick state forces the "X seconds ago" label to re-render every second
  // while the panel is open. Cheap because it only runs while the panel is
  // mounted (the table keeps a single row open at a time).
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getBatchProcessLogs(token, batchId)
      .then(data => { if (!cancelled) setLogs(data); })
      .catch(e  => { if (!cancelled) setLogsErr(e.message || "Failed to load logs"); });
    getStorageInfoForBatch(token, batchId)
      .then(data => {
        if (cancelled) return;
        // Current builds answer { items, unlinkedWorkerRows }; older ones answer a bare
        // array. Accept both so a mixed-version deployment still renders the card.
        const items = Array.isArray(data) ? data : (data?.items ?? []);
        setStorageInfos(items);
        setUnlinkedRows(Array.isArray(data) ? 0 : (data?.unlinkedWorkerRows ?? 0));
      })
      .catch(e => { if (!cancelled) setStorageErr(e.message || "request failed"); });
    return () => { cancelled = true; };
  }, [batchId, token]);

  // Live progress poll — runs only while status is InProgress AND the panel
  // is open. Reads just this batch's live fields via an indexed PK lookup
  // (GetBatchProcessLive) so an open panel costs one point-read regardless of
  // table size. Also re-pulls logs so newly emitted lines appear without manual
  // refresh.
  useEffect(() => {
    if (status !== "InProgress") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await getBatchProcessLive(token, batchId);
        if (cancelled || !fresh) return;
        setLiveStep(fresh.currentStep        ?? fresh.CurrentStep        ?? null);
        setLiveDocs(fresh.docsProcessedSoFar ?? fresh.DocsProcessedSoFar ?? 0);
        setLiveTs(fresh.lastActivityAt       ?? fresh.LastActivityAt     ?? null);
        const newStatus = getStatus(fresh);
        if (newStatus !== status) setLiveStatus(newStatus);
      } catch { /* silent */ }
      try {
        const newLogs = await getBatchProcessLogs(token, batchId);
        if (!cancelled) setLogs(newLogs);
      } catch { /* silent */ }
    };
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [status, batchId, token]);

  // Liveness pill — green if last ping was within 10s, amber within 2 min, red beyond.
  const livenessSeconds = liveTs ? Math.round((Date.now() - new Date(liveTs).getTime()) / 1000) : null;
  let livenessColor = "#94a3b8";
  let livenessBg    = "#f1f5f9";
  let livenessLabel = t("batches.live.unknown", "no activity yet");
  if (livenessSeconds !== null) {
    if (livenessSeconds <= 10)        { livenessColor = "#15803d"; livenessBg = "#f0fdf4"; livenessLabel = t("batches.live.live",     "live · {{n}}s ago", { n: Math.max(0, livenessSeconds) }); }
    else if (livenessSeconds <= 120)  { livenessColor = "#b45309"; livenessBg = "#fef3c7"; livenessLabel = t("batches.live.idle",     "idle · {{n}}s ago", { n: livenessSeconds }); }
    else                              { livenessColor = "#b91c1c"; livenessBg = "#fee2e2"; livenessLabel = t("batches.live.stalled",  "stalled · {{n}} min", { n: Math.floor(livenessSeconds / 60) }); }
  }

  const hasAnything = archivePath || indexPath || startedAt || completedAt || isFailed || storageInfos?.length || logs?.length;
  const batchDuration = fmtDuration(startedAt, completedAt);

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, borderTop: "none" }}>
        <div style={{ padding: "12px 20px 14px 44px", background: "#f8fafc", borderBottom: "1.5px solid #e5e7eb", borderTop: "1px solid #e0e7ff" }}>

          {/* ── Live progress card ─────────────────────────────────────── */}
          {status === "InProgress" && (
            <div style={{ marginBottom: 10, borderRadius: 10, background: "#fff", border: "1px solid #fcd34d", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#fef3c7", borderBottom: "1px solid #fcd34d" }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: livenessColor, animation: livenessSeconds !== null && livenessSeconds <= 10 ? "pulse 1.5s infinite" : "none" }} />
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#92400e" }}>{t("batches.live.title", "Live progress")}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: livenessColor, background: livenessBg, borderRadius: 99, padding: "2px 8px", marginLeft: "auto" }}>{livenessLabel}</span>
              </div>
              <div style={{ padding: "10px 14px", display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 6, columnGap: 14, fontSize: 11.5, color: "#1e293b" }}>
                <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8" }}>{t("batches.live.step", "Step")}</span>
                <span style={{ wordBreak: "break-word" }}>{liveStep || t("batches.live.stepNone", "(no step reported yet)")}</span>
                <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8" }}>{t("batches.live.docs", "Documents")}</span>
                <span><strong style={{ color: "#0f172a" }}>{liveDocs}</strong> {t("batches.live.docsSuffix", "processed so far")}</span>
              </div>
            </div>
          )}

          {/* ── Batch-level info card ──────────────────────────────────── */}
          {(archivePath || indexPath || startedAt || completedAt) && (
            <div style={{ borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", overflow: "hidden", marginBottom: 10 }}>
              {archivePath && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#3b82f6", marginTop: 2, flexShrink: 0 }}><IconFolder /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 2 }}>Input file</div>
                    <div style={{ fontSize: 11.5, fontFamily: "monospace", color: "#1e293b", wordBreak: "break-all", lineHeight: 1.4 }}>{archivePath}</div>
                    {ownerHost && (
                      <div style={{ marginTop: 3, fontSize: 10, color: "#64748b" }}>
                        {t("batches.stagedOn", "Staged on")}{" "}
                        <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#475569" }}>{ownerHost}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {indexPath && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 14px", borderBottom: (startedAt || completedAt) ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ color: "#8b5cf6", marginTop: 2, flexShrink: 0 }}><IconFile /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 2 }}>Index file</div>
                    <div style={{ fontSize: 11.5, fontFamily: "monospace", color: "#1e293b", wordBreak: "break-all", lineHeight: 1.4 }}>{indexPath}</div>
                  </div>
                </div>
              )}
              {(startedAt || completedAt) && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px" }}>
                  <span style={{ color: "#94a3b8", flexShrink: 0 }}><IconClock /></span>
                  <span style={{ fontSize: 10, color: "#64748b" }}>{fmtTs(startedAt) ?? "—"}</span>
                  <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                  {batchDuration && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: "#6366f1", background: "#ede9fe", borderRadius: 99, padding: "2px 8px", flexShrink: 0 }}>
                      <IconZap />{batchDuration}
                    </span>
                  )}
                  <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                  <span style={{ fontSize: 10, color: "#64748b" }}>{fmtTs(completedAt) ?? "—"}</span>
                </div>
              )}
            </div>
          )}

          {/* ── Error ─────────────────────────────────────────────────── */}
          {(errorMsg || isFailed) && (
            <div style={{ marginBottom: 10, borderRadius: 10, background: "#fff", border: "1px solid #fecdd3", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#fee2e2", borderBottom: "1px solid #fecdd3" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#b91c1c" }}>Processing Error</span>
              </div>
              <pre style={{ margin: 0, padding: "10px 14px", fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "monospace", color: "#7f1d1d", lineHeight: 1.6 }}>
                {errorMsg || "No error details captured for this batch."}
              </pre>
            </div>
          )}

          {/* ── Storage info ───────────────────────────────────────────── */}
          <StorageInfoPanel
            storageInfos={storageInfos}
            error={storageErr}
            unlinkedWorkerRows={unlinkedRows}
            expectsStorage={status === "Completed"}
          />

          {/* ── Logs ──────────────────────────────────────────────────── */}
          {logsErr && <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>Could not load processing logs: {logsErr}</div>}
          {logs && logs.length > 0 && (
            <div style={{ marginTop: 10, borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderBottom: "1px solid #f1f5f9" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#64748b" }}>Processing Log</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", background: "#f1f5f9", borderRadius: 99, padding: "0 6px" }}>{logs.length}</span>
              </div>
              <div style={{ padding: "6px 10px" }}>
                {logs.map(log => {
                  const s = LOG_LEVEL_STYLE[log.level] || LOG_LEVEL_STYLE.Info;
                  const ts = new Date(log.timestamp).toLocaleString();
                  const isOpen = expanded[log.id];
                  return (
                    <div key={log.id} style={{ marginBottom: 4, borderRadius: 6, border: `1px solid ${s.border}`, background: s.bg, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 10px", cursor: log.details ? "pointer" : "default" }}
                        onClick={() => log.details && setExpanded(p => ({ ...p, [log.id]: !p[log.id] }))}>
                        <span style={{ fontSize: 9, fontWeight: 800, color: "white", background: s.color, textTransform: "uppercase", letterSpacing: "0.06em", padding: "1px 5px", borderRadius: 4, flexShrink: 0, marginTop: 1 }}>{log.level}</span>
                        <span style={{ fontSize: 11.5, color: "#1e293b", flex: 1, wordBreak: "break-word" }}>{log.message}</span>
                        <span style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap", flexShrink: 0 }}>{ts}</span>
                        {log.details && <span style={{ fontSize: 11, color: s.color, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>}
                      </div>
                      {isOpen && log.details && (
                        <pre style={{ margin: 0, padding: "0 10px 8px", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "monospace", color: "#374151", borderTop: `1px solid ${s.border}` }}>
                          {log.details}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!hasAnything && <span style={{ fontSize: 12, color: "#9ca3af" }}>No additional details available.</span>}
        </div>
      </td>
    </tr>
  );
}

// ─── Per-row action buttons ────────────────────────────────────────────────────

const circleBtn = (extra = {}) => ({
  width: 30, height: 30, borderRadius: "50%", padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  ...extra,
});

const pillBtn = (extra = {}) => ({
  height: 30, padding: "0 10px", borderRadius: 15, fontSize: 11, fontWeight: 700, cursor: "pointer",
  display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
  ...extra,
});

// ─── Hard-delete confirmation modal ────────────────────────────────────────────
// Customer must type the exact word DELETE before the destructive button enables.
// The typed token is forwarded to the API and recorded in the audit log alongside
// IP, geo, user, and UTC timestamp captured server-side.
function HardDeleteConfirmModal({ open, t, title, bullets, confirmLabel, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const expected = "DELETE";

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  if (!open) return null;

  const armed = typed === expected && !busy;

  return (
    <div role="dialog" aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: "22px 24px", width: "min(520px, 92vw)",
          boxShadow: "0 18px 50px rgba(0,0,0,0.25)", border: "1.5px solid #fca5a5",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{
            width: 32, height: 32, borderRadius: "50%", background: "#fee2e2", color: "#b91c1c",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <TrashIcon size={16} />
          </span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{title}</h2>
        </div>

        <p style={{ margin: "6px 0 8px 0", fontSize: 13, color: "#b91c1c", fontWeight: 600 }}>
          {t("batches.hardDelete.warning", "This action cannot be undone.")}
        </p>

        <ul style={{ margin: "8px 0 14px 0", paddingLeft: 20, fontSize: 13, color: "#374151", lineHeight: 1.55 }}>
          {bullets.map((line, i) => (<li key={i}>{line}</li>))}
          <li>{t("batches.hardDelete.dbStays", "The database record stays for audit and report linkage.")}</li>
          <li>{t("batches.hardDelete.audited", "This action is audited (your user, IP and timestamp are recorded).")}</li>
        </ul>

        <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
          {t("batches.hardDelete.typeToConfirm", "Type {{token}} to confirm:", { token: expected })}
        </label>
        <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)}
          placeholder={expected} autoFocus
          style={{
            width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 14, fontFamily: "monospace",
            border: `1.5px solid ${armed ? "#86efac" : "#e5e7eb"}`, borderRadius: 8, outline: "none",
          }} />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff",
              color: "#374151", fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer",
            }}>
            {t("batches.cancel", "Cancel")}
          </button>
          <button type="button" onClick={() => onConfirm(expected)} disabled={!armed}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "none",
              background: armed ? "#dc2626" : "#fca5a5", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: armed ? "pointer" : "not-allowed",
            }}>
            {busy ? t("batches.deletingEllipsis", "Deleting…") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowActions({ status, batchId, bp, t }) {
  const { restoringId, handleRestore, setConfirmPermanentId,
          permanentDeletingId, confirmId, setConfirmId,
          deletingId, handleDelete } = bp;

  if (status === "Deleted") {
    // Single trash icon → opens the typed-confirmation modal (handled at the page
    // level via setConfirmPermanentId). No inline pill confirmation — hard delete
    // requires more friction than a single click can provide.
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <button type="button" title={t("batches.restoreTitle", "Restore to Pending")}
          disabled={restoringId === batchId} onClick={() => handleRestore(batchId)}
          style={circleBtn({ border: "1.5px solid #86efac", background: "#f0fdf4", color: "#15803d", cursor: restoringId === batchId ? "not-allowed" : "pointer", opacity: restoringId === batchId ? 0.5 : 1 })}>
          <RestoreIcon size={14} />
        </button>
        <button type="button" title={t("batches.deleteFilesTitle", "Delete archive files (DB row stays for audit)")}
          disabled={permanentDeletingId === batchId} onClick={() => setConfirmPermanentId(batchId)}
          style={circleBtn({ border: "1.5px solid #fca5a5", background: "#fff5f5", color: "#b91c1c", cursor: permanentDeletingId === batchId ? "not-allowed" : "pointer", opacity: permanentDeletingId === batchId ? 0.6 : 1 })}>
          <TrashIcon size={13} />
        </button>
      </span>
    );
  }

  if (["Pending", "InProgress", "Failed", "Completed"].includes(status)) {
    // Active list: trash icon → soft delete (hide docs, keep files). Hard delete is
    // available from the trash view, where the same icon means "remove files now".
    return confirmId === batchId ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <button type="button" title={t("batches.confirmDelete", "Confirm delete")}
          disabled={deletingId === batchId} onClick={() => handleDelete(batchId, "soft")}
          style={circleBtn({ border: "none", background: "#ef4444", color: "#fff", cursor: deletingId === batchId ? "not-allowed" : "pointer", opacity: deletingId === batchId ? 0.6 : 1 })}>
          <CheckIcon size={13} />
        </button>
        <button type="button" title={t("batches.cancelTitle", "Cancel")} onClick={() => setConfirmId(null)}
          style={circleBtn({ border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280" })}>
          <XIcon size={13} />
        </button>
      </span>
    ) : (
      <button type="button" title={t("batches.deleteTitle", "Delete (move to trash)")} onClick={() => setConfirmId(batchId)}
        style={circleBtn({ border: "1.5px solid #fca5a5", background: "#fff5f5", color: "#b91c1c" })}>
        <TrashIcon size={13} />
      </button>
    );
  }

  return null;
}

// ─── Bulk action bar ───────────────────────────────────────────────────────────

function BulkActionBar({ bp, t }) {
  const { selectedIds, showDeleted, bulkRestoring, handleBulkRestore, permanentBulkDeleting,
          setConfirmBulkPermanent, bulkDeleting, handleBulkDelete, exitSelectMode } = bp;
  const count = selectedIds.size;

  const actionBtn = (label, onClick, bg, busy) => (
    <button type="button" disabled={busy} onClick={onClick} style={{
      padding: "6px 18px", borderRadius: 7, border: "none", background: bg, color: "#fff",
      fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.65 : 1,
    }}>
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", marginBottom: 8, borderRadius: 8, background: "#1e293b", color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{t("batches.selectedCount", { count, defaultValue: "{{count}} batch(es) selected" })}</span>

      {showDeleted ? (
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
          {actionBtn(bulkRestoring ? t("batches.restoringEllipsis", "Restoring…") : t("batches.restoreSelected", { count, defaultValue: "Restore {{count}} selected" }), handleBulkRestore, "#22c55e", bulkRestoring)}
          {actionBtn(permanentBulkDeleting ? t("batches.deletingEllipsis", "Deleting…") : t("batches.deleteFiles", "Delete files"), () => setConfirmBulkPermanent(true), "#ef4444", permanentBulkDeleting)}
        </span>
      ) : (
        <span style={{ marginLeft: "auto" }}>
          {actionBtn(bulkDeleting ? t("batches.deletingEllipsis", "Deleting…") : t("batches.deleteSelected", { count, defaultValue: "Delete {{count}} selected" }), () => handleBulkDelete("soft"), "#ef4444", bulkDeleting)}
        </span>
      )}

      <button type="button" onClick={exitSelectMode} style={{ padding: "6px 12px", borderRadius: 7, border: "1.5px solid #475569", background: "transparent", color: "#cbd5e1", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        {t("batches.clear", "Clear")}
      </button>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function useResizableColWidth(storageKey, defaultWidth, min = 80, max = 900) {
  const [width, setWidth] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(storageKey), 10);
      return Number.isFinite(n) && n >= min && n <= max ? n : defaultWidth;
    } catch { return defaultWidth; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, String(width)); } catch { /* ignore */ }
  }, [storageKey, width]);

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev) => {
      const next = Math.max(min, Math.min(max, startW + (ev.clientX - startX)));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const reset = () => setWidth(defaultWidth);
  return { width, startResize, reset };
}

function ColResizeHandle({ onMouseDown, onDoubleClick }) {
  return (
    <span
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      title="Drag to resize · double-click to reset"
      style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 8, cursor: "col-resize", userSelect: "none" }}
    />
  );
}

// Clickable header label that drives server-side sorting. Shows the active
// direction (▲/▼) on the sorted column and a faint ↕ hint on the others. Rendered
// as an inline span (not the whole <th>) so it never collides with a column's
// resize handle.
function SortLabel({ label, col, bp }) {
  const active = bp.sort === col;
  return (
    <span
      onClick={() => bp.toggleSort(col)}
      title="Sort"
      style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      {label}
      <span style={{ fontSize: 9, fontWeight: 700, color: active ? "#4f46e5" : "#cbd5e1" }}>
        {active ? (bp.dir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </span>
  );
}

export default function BatchProcesses() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const bp = useBatchProcesses(token);

  const fileCol = useResizableColWidth("batchProcesses.fileColWidth", 220, 100);
  const dbCol   = useResizableColWidth("batchProcesses.dbColWidth",   150, 80);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <div className="page-container">

        {/* Page header — gains a prominent "Trash" indicator + Exit button when viewing deleted */}
        {bp.showDeleted && (
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 16px", marginBottom: 14, borderRadius: 8,
            background: "#fef2f2", border: "1.5px solid #fca5a5", color: "#991b1b",
          }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: "#fee2e2" }}>
              <TrashIcon size={16} />
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {t("batches.trashBanner.title", "You are viewing the Trash")}
              </div>
              <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 2 }}>
                {t("batches.trashBanner.subtitle", "These batches are soft-deleted. Documents from these batches are no longer searchable. Files are queued for permanent removal by the worker.")}
              </div>
            </div>
            <button type="button" onClick={bp.toggleShowDeleted}
              style={{
                padding: "6px 12px", borderRadius: 7, border: "1.5px solid #fca5a5",
                background: "#fff", color: "#b91c1c", fontSize: 12, fontWeight: 700, cursor: "pointer",
                whiteSpace: "nowrap",
              }}>
              {t("batches.trashBanner.exit", "Exit Trash")}
            </button>
          </div>
        )}

        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <polygon points="12 2 22 7 12 12 2 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
          }
          title={
            bp.showDeleted ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 800,
                  background: "#dc2626", color: "#fff", letterSpacing: 0.5, textTransform: "uppercase",
                }}>
                  <TrashIcon size={11} /> {t("batches.trashBanner.pill", "Trash")}
                </span>
                {t("admin.batches", { defaultValue: "Ingestion Batches" })}
              </span>
            ) : (
              t("admin.batches", { defaultValue: "Ingestion Batches" })
            )
          }
          subtitle={bp.showDeleted
            ? t("batches.trashBanner.subtitle", "These batches are soft-deleted. Documents from these batches are no longer searchable. Files are queued for permanent removal by the worker.")
            : t("admin.batchesSubtitle", { defaultValue: "Monitor ingestion batches and their processing status." })}
          onRefresh={() => bp.setTick(n => n + 1)}
          refreshing={bp.loading}
          logo={<LayersArt />}
        />

        {/* Stats — hidden in trash view (active-list status filters don't apply there) */}
        {!bp.showDeleted && (
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <StatCard label="Total"       value={bp.stats.total}      color="#4b5563"                    bg="#f9fafb"                   active={bp.statusFilter === "ALL"}        onClick={() => bp.setStatusFilter("ALL")}        />
            <StatCard label="Pending"     value={bp.stats.pending}    color={STATUS_META.pending.color}    bg={STATUS_META.pending.bg}    active={bp.statusFilter === "Pending"}    onClick={() => bp.setStatusFilter("Pending")}    />
            <StatCard label="In Progress" value={bp.stats.inProgress} color={STATUS_META.inprogress.color} bg={STATUS_META.inprogress.bg} active={bp.statusFilter === "InProgress"} onClick={() => bp.setStatusFilter("InProgress")} />
            <StatCard label="Completed"   value={bp.stats.completed}  color={STATUS_META.completed.color}  bg={STATUS_META.completed.bg}  active={bp.statusFilter === "Completed"}  onClick={() => bp.setStatusFilter("Completed")}  />
            <StatCard label="Failed"      value={bp.stats.failed}     color={STATUS_META.failed.color}     bg={STATUS_META.failed.bg}     active={bp.statusFilter === "Failed"}     onClick={() => bp.setStatusFilter("Failed")}     />
          </div>
        )}

        {/* Toolbar: status segmented control + search + date range */}
        <div className="asf-card" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 16px" }}>
          {/* Status filter is for active-list states (Pending/InProgress/etc.) —
              it doesn't apply in trash view, so hide it there. */}
          {!bp.showDeleted && (
            <SegControl
              value={bp.statusFilter}
              onChange={bp.setStatusFilter}
              options={STATUS_FILTER_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
            />
          )}

          {/* Search */}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <svg style={{ position: "absolute", left: 11, pointerEvents: "none", color: "#94a3b8" }} width="14" height="14" viewBox="0 0 15 15" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input className="asf-input" type="text" placeholder="Search…" value={bp.search}
              onChange={e => bp.setSearch(e.target.value)} style={{ width: 190, paddingLeft: 33, paddingRight: 26 }} />
            {bp.search && (
              <button type="button" onClick={() => bp.setSearch("")}
                style={{ position: "absolute", right: 10, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
            )}
          </div>

          {/* Date range */}
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span className="asf-lbl" style={{ margin: 0 }}>From</span>
            <input className="asf-input" type="date" value={bp.dateFrom} onChange={e => bp.setDateFrom(e.target.value)} style={{ width: 150 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span className="asf-lbl" style={{ margin: 0 }}>To</span>
            <input className="asf-input" type="date" value={bp.dateTo} onChange={e => bp.setDateTo(e.target.value)} style={{ width: 150 }} />
          </div>
          {(bp.search || bp.dateFrom || bp.dateTo) && (
            <button type="button" className="asf-btn" onClick={() => { bp.setSearch(""); bp.setDateFrom(""); bp.setDateTo(""); }}
              style={{ padding: "9px 16px", fontSize: 13 }}>
              Clear
            </button>
          )}

          <span className="asf-count" style={{ marginLeft: "auto" }}>
            {bp.loading ? "Loading…" : t("batches.pager.total", { total: bp.total.toLocaleString(), defaultValue: "{{total}} total" })}
          </span>
          {bp.selectableIds.length > 0 && (
            bp.selectMode
              ? <button type="button" className="asf-cta" onClick={bp.exitSelectMode} style={{ padding: "9px 18px", fontSize: 13 }}>Cancel</button>
              : <button type="button" className="asf-btn" onClick={() => bp.setSelectMode(true)} style={{ padding: "9px 18px", fontSize: 13 }}>Select</button>
          )}
        </div>

        {/* Error */}
        {bp.error && (
          <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "#fee2e2", color: "#b91c1c", fontSize: 13, border: "1px solid #fecaca" }}>
            {bp.error}
            <ErrorDetailsAccordion details={bp.rawErrorDetails} />
          </div>
        )}

        {/* Bulk action bar */}
        {bp.selectedIds.size > 0 && <BulkActionBar bp={bp} t={t} />}

        {/* Table */}
        <div className="logs-card" style={{ maxHeight: "calc(100vh - 360px)", overflow: "auto" }}>
          {!bp.hasItems && !bp.loading ? (
            <div style={{ padding: 48, textAlign: "center", color: "#9ca3af" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>No batches found</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {bp.showDeleted ? "No deleted batches." : bp.statusFilter !== "ALL" ? `No batches with status "${bp.statusFilter}"` : "No recent batches were created yet."}
              </div>
            </div>
          ) : bp.loading && !bp.hasItems ? (
            <div style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Loading batches…</div>
          ) : (
            <table className="logs-table" style={{ minWidth: 32 + (bp.selectMode ? 36 : 0) + 64 + dbCol.width + fileCol.width + 80 + 170 + 120 + 230 + 130 + 160 }}>
              <thead>
                <tr>
                  <th style={{ width: 32, padding: "0 4px" }} />
                  {bp.selectMode && (
                    <th style={{ width: 36, textAlign: "center", paddingRight: 0 }}>
                      <input type="checkbox" checked={bp.allSelected}
                        ref={el => { if (el) el.indeterminate = bp.someSelected && !bp.allSelected; }}
                        onChange={bp.toggleAll} style={{ cursor: "pointer", width: 14, height: 14 }} />
                    </th>
                  )}
                  <th style={{ width: 64 }}><SortLabel label="ID" col="id" bp={bp} /></th>
                  <th style={{ width: dbCol.width, position: "relative" }}>
                    <SortLabel label="Database" col="database" bp={bp} />
                    <ColResizeHandle onMouseDown={dbCol.startResize} onDoubleClick={dbCol.reset} />
                  </th>
                  <th style={{ width: fileCol.width, position: "relative" }}>
                    File
                    <ColResizeHandle onMouseDown={fileCol.startResize} onDoubleClick={fileCol.reset} />
                  </th>
                  <th style={{ width: 80 }}>Type</th>
                  <th style={{ width: 170 }}><SortLabel label="Submitted" col="created" bp={bp} /></th>
                  <th style={{ width: 120 }}><SortLabel label="Status" col="status" bp={bp} /></th>
                  <th style={{ width: 230, paddingLeft: 24 }}>Pipeline</th>
                  <th style={{ width: 130 }}>User</th>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {bp.items.map(b => {
                  const batchId     = b.id ?? b.Id;
                  const status      = getStatus(b);
                  const isDeleted   = b.isDeleted   ?? b.IsDeleted   ?? false;
                  const isProcessed = b.isProcessed ?? b.IsProcessed ?? false;
                  const created     = getCreatedDate(b);
                  const ago         = timeAgo(b.createdDate || b.CreatedDate);
                  const isExpanded  = bp.expandedId === batchId;
                  // total columns for the detail panel colspan
                  const colCount    = 10 + (bp.selectMode ? 1 : 0);
                  return (
                    <React.Fragment key={batchId}>
                      <tr style={{ opacity: isDeleted ? 0.55 : 1, transition: "background 0.1s", background: isExpanded ? "#f0f4ff" : bp.selectedIds.has(batchId) ? "#eff6ff" : undefined }}>
                        <td style={{ padding: "0 4px", width: 32, textAlign: "center" }}>
                          <button type="button" onClick={() => bp.toggleExpanded(batchId)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 4, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
                            title={isExpanded ? "Collapse details" : "Expand details"}>
                            <ChevronIcon expanded={isExpanded} />
                          </button>
                        </td>
                        {bp.selectMode && (
                          <td style={{ textAlign: "center", paddingRight: 0 }}>
                            {(bp.showDeleted || ["Pending", "Failed", "Completed"].includes(status)) && (
                              <input type="checkbox" checked={bp.selectedIds.has(batchId)} onChange={() => bp.toggleOne(batchId)} style={{ cursor: "pointer", width: 14, height: 14 }} />
                            )}
                          </td>
                        )}
                        <td style={{ fontWeight: 700, color: "#6b7280", fontSize: 13 }}>#{batchId}</td>
                        <td style={{ fontWeight: 500, fontSize: 13, width: dbCol.width, maxWidth: dbCol.width, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={getDatabaseName(b) || ""}>{getDatabaseName(b) || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                        <td style={{ fontSize: 12, color: "#374151", fontFamily: "monospace", width: fileCol.width, maxWidth: fileCol.width, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={getArchiveFileName(b)}>{getArchiveFileName(b) || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                        <td><FileTypeBadge type={getFileType(b)} /></td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div style={{ fontSize: 13, color: "#111", fontWeight: 500 }}>{created.date}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{created.time}{ago ? ` · ${ago}` : ""}</div>
                        </td>
                        <td><StatusBadge status={status} /></td>
                        <td style={{ paddingLeft: 24 }}><PipelineSteps status={status} isProcessed={isProcessed} isDeleted={isDeleted} /></td>
                        <td style={{ fontSize: 12, color: "#374151" }}>{getRegisteringUser(b) || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap", width: 160, minWidth: 160 }}>
                          <RowActions status={status} batchId={batchId} bp={bp} t={t} />
                        </td>
                      </tr>
                      {isExpanded && <BatchDetailPanel batch={b} colSpan={colCount} token={token} t={t} />}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — appears once there is more than one page. Browsing, sorting
            and filtering are all server-side, so this reaches every record without
            ever loading more than one page into the browser. */}
        {bp.total > bp.pageSize && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              {bp.loading ? "…" : t("batches.pager.summary", {
                start: (bp.page * bp.pageSize + 1).toLocaleString(),
                end: Math.min((bp.page + 1) * bp.pageSize, bp.total).toLocaleString(),
                total: bp.total.toLocaleString(),
                defaultValue: "Showing {{start}}–{{end}} of {{total}}",
              })}
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button className="icon-btn" onClick={() => bp.setPage(0)} disabled={bp.page === 0} style={{ padding: "4px 10px", fontSize: 13 }}>«</button>
              <button className="icon-btn" onClick={() => bp.setPage(p => Math.max(0, p - 1))} disabled={bp.page === 0} style={{ padding: "4px 12px", fontSize: 13 }}>‹ {t("batches.pager.prev", "Previous")}</button>
              <span style={{ display: "flex", alignItems: "center", padding: "4px 12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#374151" }}>
                {t("batches.pager.pageOf", { page: bp.page + 1, total: bp.totalPages, defaultValue: "Page {{page}} of {{total}}" })}
              </span>
              <button className="icon-btn" onClick={() => bp.setPage(p => Math.min(bp.totalPages - 1, p + 1))} disabled={bp.page >= bp.totalPages - 1} style={{ padding: "4px 12px", fontSize: 13 }}>{t("batches.pager.next", "Next")} ›</button>
              <button className="icon-btn" onClick={() => bp.setPage(bp.totalPages - 1)} disabled={bp.page >= bp.totalPages - 1} style={{ padding: "4px 10px", fontSize: 13 }}>»</button>
            </div>
          </div>
        )}

      </div>

      {/* Empty Trash button — opens typed-confirmation modal */}
      {bp.showDeleted && (
        <div style={{ position: "fixed", bottom: 92, right: 24, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <button type="button" disabled={bp.stats.deleted === 0} onClick={() => bp.setConfirmEmptyTrash(true)}
            style={{ padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, border: "1.5px solid #fca5a5", background: "#fff5f5", color: "#b91c1c", whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", cursor: bp.stats.deleted === 0 ? "not-allowed" : "pointer", opacity: bp.stats.deleted === 0 ? 0.5 : 1 }}>
            {t("batches.emptyTrash", "Empty Trash")}
          </button>
        </div>
      )}

      {/* Hard-delete confirmation: single trashed batch */}
      <HardDeleteConfirmModal
        open={bp.confirmPermanentId !== null && bp.confirmPermanentId !== undefined}
        t={t}
        title={t("batches.hardDelete.singleTitle", "Permanently delete files for batch #{{id}}?", { id: bp.confirmPermanentId ?? "" })}
        bullets={[
          t("batches.hardDelete.bulletFiles", "All files in the batch folder (.dat, .afp, .RSC, etc.) will be removed from disk."),
          t("batches.hardDelete.bulletInput", "The original input file will also be deleted."),
          t("batches.hardDelete.bulletDocs", "Documents from this batch are already hidden from search."),
        ]}
        confirmLabel={t("batches.hardDelete.confirmFiles", "Delete files")}
        busy={bp.permanentDeletingId === bp.confirmPermanentId}
        onCancel={() => bp.setConfirmPermanentId(null)}
        onConfirm={(token) => bp.handlePermanentDelete(bp.confirmPermanentId, token)} />

      {/* Hard-delete confirmation: empty trash (all soft-deleted batches) */}
      <HardDeleteConfirmModal
        open={bp.confirmEmptyTrash}
        t={t}
        title={t("batches.hardDelete.emptyTitle", "Permanently delete files for {{count}} batch(es)?", { count: bp.stats.deleted })}
        bullets={[
          t("batches.hardDelete.bulletFilesAll", "All files for every batch in the trash will be removed from disk."),
          t("batches.hardDelete.bulletInputAll", "Original input files will also be deleted."),
          t("batches.hardDelete.bulletDocsAll", "Documents from these batches are already hidden from search."),
        ]}
        confirmLabel={t("batches.hardDelete.confirmEmpty", "Delete all files")}
        busy={bp.emptyingTrash}
        onCancel={() => bp.setConfirmEmptyTrash(false)}
        onConfirm={(token) => bp.handleEmptyTrash(token)} />

      {/* Hard-delete confirmation: bulk action on selected trashed batches */}
      <HardDeleteConfirmModal
        open={bp.confirmBulkPermanent}
        t={t}
        title={t("batches.hardDelete.bulkTitle", "Permanently delete files for {{count}} selected batch(es)?", { count: bp.selectedIds.size })}
        bullets={[
          t("batches.hardDelete.bulletFilesSel", "All files for the selected batches will be removed from disk."),
          t("batches.hardDelete.bulletInputSel", "Original input files will also be deleted."),
          t("batches.hardDelete.bulletDocsSel", "Documents from the selected batches are already hidden from search."),
        ]}
        confirmLabel={t("batches.hardDelete.confirmBulk", "Delete selected files")}
        busy={bp.permanentBulkDeleting}
        onCancel={() => bp.setConfirmBulkPermanent(false)}
        onConfirm={async (token) => { bp.setConfirmBulkPermanent(false); await bp.handlePermanentBulkDelete(token); }} />

      {/* Trash FAB */}
      <button type="button" onClick={bp.toggleShowDeleted}
        title={bp.showDeleted ? "Hide deleted batches" : `Show deleted batches${bp.stats.deleted > 0 ? ` (${bp.stats.deleted})` : ""}`}
        style={{ position: "fixed", bottom: 32, right: 32, width: 48, height: 48, borderRadius: "50%", zIndex: 200, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", border: bp.showDeleted ? "2px solid #374151" : "2px solid #e5e7eb", background: bp.showDeleted ? "#374151" : "#fff", color: bp.showDeleted ? "#fff" : "#6b7280", boxShadow: bp.showDeleted ? "0 4px 20px rgba(55,65,81,0.35)" : "0 4px 16px rgba(0,0,0,0.12)" }}>
        <TrashIcon size={18} />
        {bp.stats.deleted > 0 && (
          <span style={{ position: "absolute", top: -5, right: -5, borderRadius: "50%", minWidth: 18, height: 18, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", border: "2px solid #fff", boxSizing: "border-box", background: bp.showDeleted ? "#fff" : "#6b7280", color: bp.showDeleted ? "#374151" : "#fff" }}>
            {bp.stats.deleted}
          </span>
        )}
      </button>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .logs-table tbody tr:hover td { background: #f8faff !important; }
      `}</style>
    </div>
  );
}
